/** The Insights charts' chart language, as plain functions (no React): which
 *  colours a chart may use, how its numbers read, which categories get their
 *  own bar, how numbers are bucketed, the one-line takeaway a template's
 *  results lead with, and how an axis runs in a right-to-left language.
 *  Unit-tested in tests/unit/insights/template_chart_model.test.mjs.
 *
 *  Colour follows the dataviz method: a fixed categorical order validated for
 *  colour-blind readers (Machado 2009 simulation, OKLab distance), a
 *  one-hue sequential ramp for magnitude, and the organization's brand colour
 *  leading wherever it can - moved into the legible lightness band when it is
 *  not, and never at the price of two series that read alike. The Templates
 *  tab draws with it today (useChartColors). */
import type { ResponseType } from '../types/insights';

export type Mode = 'light' | 'dark';

// ── Colour science ───────────────────────────────────────────────────────

/** The reference categorical order (dataviz palette.md), per mode. */
export const REFERENCE: Record<Mode, string[]> = {
  light: ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#008300', '#6250d6', '#e34948'],
  dark: ['#3987e5', '#d95926', '#199e70', '#c98500', '#d55181', '#008300', '#9085e9', '#e66767'],
};
/** Where a categorical colour must sit (OKLCH L) to read on the mode's surface. */
export const BAND: Record<Mode, [number, number]> = { light: [0.43, 0.77], dark: [0.48, 0.67] };
export const CHROMA_FLOOR = 0.10;
export const MAX_SERIES = 8;
/** The tail folded into one mark ("Other"): neutral, never a series hue. */
export const OTHER_GREY: Record<Mode, string> = { light: '#8f8d86', dark: '#7d7b74' };
const CVD_TARGET = 8;      // OKLab ΔE×100, worst of protan / deutan
const NORMAL_FLOOR = 15;   // OKLab ΔE×100, unsimulated vision
/** A brand-led palette must keep at least this many colours, or the brand
 *  leads only the one-colour charts and the reference order serves the rest. */
const MIN_SERIES = 6;

type Vec = [number, number, number];

const MACHADO: Record<'protan' | 'deutan', Vec[]> = {
  protan: [[0.152286, 1.052583, -0.204868], [0.114503, 0.786281, 0.099216], [-0.003882, -0.048116, 1.051998]],
  deutan: [[0.367322, 0.860646, -0.227968], [0.280085, 0.672501, 0.047413], [-0.011820, 0.042940, 0.968881]],
};

/** '#abc', '#aabbcc', 'rgb(…)' → '#aabbcc' (CSS colours as a brand gives them), or null. */
function parseHex(color: string | null | undefined): string | null {
  const c = (color || '').trim().toLowerCase();
  let m = /^#([0-9a-f]{3})$/.exec(c);
  if (m) return `#${m[1].split('').map((x) => x + x).join('')}`;
  m = /^#([0-9a-f]{6})([0-9a-f]{2})?$/.exec(c);
  if (m) return `#${m[1]}`;
  const rgb = /^rgba?\(\s*(\d+)[\s,]+(\d+)[\s,]+(\d+)/.exec(c);
  if (rgb) return `#${rgb.slice(1, 4).map((x) => Math.min(255, Number(x)).toString(16).padStart(2, '0')).join('')}`;
  return null;
}

const toLinear = (c: number) => (c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
const toGamma = (c: number) => (c <= 0.0031308 ? 12.92 * c : 1.055 * c ** (1 / 2.4) - 0.055);

function linear(hex: string): Vec {
  const h = hex.replace('#', '');
  return [0, 2, 4].map((i) => toLinear(parseInt(h.slice(i, i + 2), 16) / 255)) as Vec;
}

function oklabFromLinear([r, g, b]: Vec): Vec {
  const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
  const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
  const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);
  return [
    0.2104542553 * l + 0.7936177850 * m - 0.0040720468 * s,
    1.9779984951 * l - 2.4285922050 * m + 0.4505937099 * s,
    0.0259040371 * l + 0.7827717662 * m - 0.8086757660 * s,
  ];
}

function linearFromOklab([L, a, b]: Vec): Vec {
  const l = (L + 0.3963377774 * a + 0.2158037573 * b) ** 3;
  const m = (L - 0.1055613458 * a - 0.0638541728 * b) ** 3;
  const s = (L - 0.0894841775 * a - 1.2914855480 * b) ** 3;
  return [
    4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
    -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
    -0.0041960863 * l - 0.7034186147 * m + 1.7076147010 * s,
  ];
}

/** OKLCH of a colour: [lightness, chroma, hue in degrees]. */
export function oklch(hex: string): Vec {
  const [L, a, b] = oklabFromLinear(linear(hex));
  return [L, Math.hypot(a, b), ((Math.atan2(b, a) * 180) / Math.PI + 360) % 360];
}

/** The sRGB colour nearest an OKLCH point: chroma is reduced until it fits. */
function fromOklch(L: number, C: number, h: number): string {
  const rad = (h * Math.PI) / 180;
  let chroma = C;
  for (let i = 0; i < 40; i += 1) {
    const rgb = linearFromOklab([L, chroma * Math.cos(rad), chroma * Math.sin(rad)]);
    if (rgb.every((c) => c >= -1e-4 && c <= 1 + 1e-4) || chroma <= 0) {
      return `#${rgb.map((c) => Math.round(Math.max(0, Math.min(1, toGamma(Math.max(0, c)))) * 255)
        .toString(16).padStart(2, '0')).join('')}`;
    }
    chroma = Math.max(0, chroma - C / 40);
  }
  return '#808080';
}

function simulate(hex: string, kind: 'protan' | 'deutan'): Vec {
  const [r, g, b] = linear(hex);
  return MACHADO[kind].map((row) => Math.max(0, Math.min(1, row[0] * r + row[1] * g + row[2] * b))) as Vec;
}

/** OKLab distance ×100 between two colours, as a normal or a colour-blind eye sees them. */
function deltaE(a: string, b: string, kind?: 'protan' | 'deutan'): number {
  const x = oklabFromLinear(kind ? simulate(a, kind) : linear(a));
  const y = oklabFromLinear(kind ? simulate(b, kind) : linear(b));
  return 100 * Math.hypot(x[0] - y[0], x[1] - y[1], x[2] - y[2]);
}

/** Two colours a reader can tell apart, with full colour vision and without. */
export function distinct(a: string, b: string): boolean {
  return deltaE(a, b) >= NORMAL_FLOOR && Math.min(deltaE(a, b, 'protan'), deltaE(a, b, 'deutan')) >= CVD_TARGET;
}

/** A brand colour made fit to lead a chart: inside the mode's lightness band
 *  and above the chroma floor, hue kept. A grey brand (no hue to keep) yields
 *  null - the reference order then leads. */
function chartReady(color: string | null | undefined, mode: Mode): string | null {
  const hex = parseHex(color);
  if (!hex) return null;
  const [L, C, h] = oklch(hex);
  if (C < 0.04) return null;
  const [lo, hi] = BAND[mode];
  const target = Math.min(hi - 0.02, Math.max(lo + 0.02, L));
  const fitted = target === L && C >= CHROMA_FLOOR ? hex : fromOklch(target, Math.max(C, CHROMA_FLOOR + 0.02), h);
  const [L2, C2] = oklch(fitted);
  return L2 >= lo && L2 <= hi && C2 >= CHROMA_FLOOR ? fitted : null;
}

/** The categorical colours, in their fixed order: the brand's colour first when
 *  it can lead, then the reference order minus any slot that would read like
 *  the brand's or like its new neighbour (the reference is validated on
 *  neighbours: stacks, bars and lines put series side by side). Never more
 *  than MAX_SERIES; never generated. */
export function categoricalPalette(brand: string | null | undefined, mode: Mode): string[] {
  const lead = chartReady(brand, mode);
  const chosen: string[] = lead ? [lead] : [];
  for (const slot of REFERENCE[mode]) {
    if (chosen.length >= MAX_SERIES) break;
    const previous = chosen[chosen.length - 1];
    if ((!lead || distinct(lead, slot)) && (!previous || distinct(previous, slot))) chosen.push(slot);
  }
  return chosen.length >= MIN_SERIES ? chosen : [...REFERENCE[mode]];
}

/** The one colour of a one-series chart (bars of categories, a score's
 *  columns): the brand's when it is fit to lead, else the reference's first. */
export function leadColour(brand: string | null | undefined, mode: Mode): string {
  return chartReady(brand, mode) || REFERENCE[mode][0];
}

/** A one-hue ramp for magnitude: `steps` colours of the lead hue, light to
 *  dark on a light surface and dark to light on a dark one (more always reads
 *  as more contrast). Values are labelled, so colour never carries a number
 *  alone. */
export function sequentialRamp(lead: string, steps: number, mode: Mode): string[] {
  const [, C, h] = oklch(lead);
  const [from, to] = mode === 'light' ? [0.80, 0.40] : [0.42, 0.84];
  const n = Math.max(1, steps);
  return Array.from({ length: n }, (_, i) => {
    const L = n === 1 ? (from + to) / 2 : from + ((to - from) * i) / (n - 1);
    return fromOklch(L, Math.max(C, 0.09), h);
  });
}

// ── Numbers ──────────────────────────────────────────────────────────────

const formatters = new Map<string, Intl.NumberFormat>();
function numberFormat(locale: string, options: Intl.NumberFormatOptions = {}): Intl.NumberFormat {
  const key = `${locale}|${JSON.stringify(options)}`;
  let format = formatters.get(key);
  if (!format) {
    format = new Intl.NumberFormat(locale, options);
    formatters.set(key, format);
  }
  return format;
}

export function formatCount(n: number, locale: string): string {
  return numberFormat(locale).format(n);
}

/** A share of a whole: whole percents, "<1%" for a sliver (never "0%" for
 *  something that is there), "0%" only for nothing. */
export function formatShare(part: number, whole: number, locale: string): string {
  const percent = numberFormat(locale, { style: 'percent', maximumFractionDigits: 0 });
  if (!whole || !part) return percent.format(0);
  const share = part / whole;
  return share < 0.005 ? `<${percent.format(0.01)}` : percent.format(share);
}

/** An average or a measured value: at most one decimal, none when whole. */
export function formatDecimal(n: number, locale: string): string {
  return numberFormat(locale, { maximumFractionDigits: 1 }).format(n);
}

// ── Categories ───────────────────────────────────────────────────────────

export interface Entry { value: string; count: number }
export interface Ranked {
  /** The bars, largest first; ties in name order. */
  shown: Entry[];
  /** The tail folded into one bar ("Other"), when it may be summed. */
  other: { count: number; members: Entry[] } | null;
  /** Categories past the shown ones when they may not be summed (several
   *  categories: one conversation can be in many). */
  hidden: number;
}

export function byCount(distribution: Record<string, number>): Entry[] {
  return Object.entries(distribution).map(([value, count]) => ({ value, count }))
    .sort((a, b) => b.count - a.count || a.value.localeCompare(b.value));
}

/** At most `limit` bars. One category per conversation: the tail is summed
 *  into "Other". Several per conversation: the tail is not a sum (it would
 *  count conversations twice) - it is left out and counted. */
export function rankCategories(distribution: Record<string, number>, limit: number, sumTail: boolean): Ranked {
  const all = byCount(distribution);
  if (all.length <= limit) return { shown: all, other: null, hidden: 0 };
  if (!sumTail) return { shown: all.slice(0, limit), other: null, hidden: all.length - limit };
  const members = all.slice(limit - 1);
  return { shown: all.slice(0, limit - 1), other: { count: members.reduce((s, e) => s + e.count, 0), members }, hidden: 0 };
}

// ── Numbers as buckets ───────────────────────────────────────────────────

/** A score's scale; its chart shows the whole range. */
export interface ScaleRange { min?: number | null; max?: number | null }

export interface Bucket {
  /** What the axis prints: "4", or "10–14" for a range. */
  label: string;
  lo: number;
  hi: number;
  count: number;
  /** What "See conversations" asks for: the value ("4"), or the range's
   *  bounds - "10..14" (both ends in) or "0.5..<1" (the end out). */
  values: string;
}

const MAX_SINGLE_VALUES = 24;
const TARGET_BUCKETS = 10;

function niceStep(span: number, target: number, integers: boolean): number {
  const raw = span / target;
  const pow = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * pow).find((s) => s >= raw) || raw;
  return integers ? Math.max(1, Math.ceil(step)) : step;
}

/** A bound as printed: as many decimals as the step needs, no more. */
function bound(n: number, step: number): string {
  const digits = Math.max(0, Math.min(6, -Math.floor(Math.log10(step))));
  return String(Number(n.toFixed(digits)));
}

/** A score's or a number's answers on a continuous axis: every whole value
 *  from the lowest to the highest (a score: its whole range) - an empty one
 *  is a zero, never a gap the eye reads as adjacent - or, when there are too
 *  many to show one by one, even ranges. */
export function numericBuckets(distribution: Record<string, number>, range?: ScaleRange): Bucket[] {
  const points = Object.entries(distribution)
    .map(([key, count]) => ({ key, n: Number(key), count }))
    .filter((p) => Number.isFinite(p.n));
  if (!points.length) return [];
  const integers = points.every((p) => Number.isInteger(p.n));
  const lo = Math.min(...points.map((p) => p.n), range?.min ?? Infinity);
  const hi = Math.max(...points.map((p) => p.n), range?.max ?? -Infinity);
  const count = (inside: typeof points) => inside.reduce((s, p) => s + p.count, 0);

  if (integers && hi - lo + 1 <= MAX_SINGLE_VALUES) {
    return Array.from({ length: hi - lo + 1 }, (_, i) => {
      const v = lo + i;
      return { label: String(v), lo: v, hi: v, count: count(points.filter((p) => p.n === v)), values: String(v) };
    });
  }
  const step = niceStep(hi - lo || 1, TARGET_BUCKETS, integers);
  const out: Bucket[] = [];
  // Whole numbers: ranges from the lowest value (a score's own minimum), both ends in.
  // Decimals: even ranges, each end out but the last.
  for (let a = integers ? lo : Math.floor(lo / step) * step; a <= hi; a += step) {
    const b = a + step;
    const last = b > hi;
    const top = integers ? Math.min(b - 1, hi) : b;
    const inside = points.filter((p) => p.n >= a && (integers || last ? p.n <= top : p.n < b));
    const label = top === a ? bound(a, step) : `${bound(a, step)}–${bound(top, step)}`;
    const values = integers || last ? `${a}..${top}` : `${a}..<${b}`;
    out.push({ label, lo: a, hi: top, count: count(inside), values: top === a && integers ? String(a) : values });
  }
  return out;
}

/** The middle answer of a distribution (its median), for a number's takeaway. */
export function median(distribution: Record<string, number>): number | null {
  const points = Object.entries(distribution).map(([k, c]) => [Number(k), c] as const)
    .filter(([n]) => Number.isFinite(n)).sort((a, b) => a[0] - b[0]);
  const total = points.reduce((s, [, c]) => s + c, 0);
  let seen = 0;
  for (const [n, c] of points) {
    seen += c;
    if (seen * 2 >= total) return n;
  }
  return null;
}

// ── The takeaway ─────────────────────────────────────────────────────────

/** The fewest answers a headline is drawn from (item 70, Segev: on staging one
 *  answered conversation headlined "most common · in 100% of conversations").
 *  Below it the chart still shows every answer; the headline says there are
 *  too few to tell. */
export const TAKEAWAY_MIN_ANSWERS = 10;

export type Takeaway =
  | { kind: 'share'; yes: number; whole: number }                   // Yes / No
  | { kind: 'top'; value: string; count: number; whole: number }    // a category, a short answer
  | { kind: 'tie'; values: string[]; count: number; whole: number } // no single most common one
  | { kind: 'average'; mean: number; median: number | null }        // a score, a number
  | { kind: 'few'; whole: number; needed: number }                  // too few answers to tell
  | null;

/** The one thing a template's results lead with - never a claim the numbers
    cannot carry: under TAKEAWAY_MIN_ANSWERS answers it says there are too few
    to tell, and when several answers share the top count none is "most common". */
export function takeaway(type: ResponseType, aggregation: {
  total?: number; distribution?: Record<string, number>; numeric_summary?: { mean: number } | null;
}): Takeaway {
  const whole = aggregation.total || 0;
  const dist = aggregation.distribution || {};
  if (!whole) return null;
  if (whole < TAKEAWAY_MIN_ANSWERS) return { kind: 'few', whole, needed: TAKEAWAY_MIN_ANSWERS };
  if (type === 'boolean') return { kind: 'share', yes: dist.true || 0, whole };
  if (type === 'scale' || type === 'numeric') {
    const mean = aggregation.numeric_summary?.mean;
    return mean == null ? null : { kind: 'average', mean, median: median(dist) };
  }
  const ranked = byCount(dist);
  const [first] = ranked;
  if (!first) return null;
  const tied = ranked.filter((e) => e.count === first.count);
  return tied.length > 1
    ? { kind: 'tie', values: tied.map((e) => e.value), count: first.count, whole }
    : { kind: 'top', value: first.value, count: first.count, whole };
}

// ── A category's colour ─────────────────────────────────────────────────

/** Each category's colour, the same in every chart and every range (item 70):
 *  its place in the template's lasting order (`order`: its own categories
 *  first, then the rest by how often they were ever answered - the server's
 *  `category_order`) picks a colour of the categorical palette, in turn; a
 *  value not in the order (or no order) follows them in the order given.
 *  Past the palette's length colours repeat across the template, never
 *  within one chart (while the palette has a colour to spare); every bar
 *  shows its category's name and number, so colour is never the only way
 *  to tell. */
export function categoryColours(values: string[], order: string[] | undefined, palette: string[]): Map<string, string> {
  // Matched as the server matches a category to the template's list: case and
  // surrounding spaces aside ("API" listed, "api" stored).
  const key = (value: string) => value.trim().toLocaleLowerCase();
  const place = new Map<string, number>();
  (order || []).forEach((value, i) => { if (!place.has(key(value))) place.set(key(value), i); });
  let next = place.size;
  for (const value of values) if (!place.has(key(value))) place.set(key(value), next++);
  const out = new Map(values.map((value) => [value, palette[(place.get(key(value)) as number) % palette.length]]));
  // Two categories of ONE chart never share a colour while the palette has one
  // to spare: the later of two (in the lasting order) takes the first free one.
  if (values.length <= palette.length) {
    const used = new Set<string>();
    const byPlace = [...values].sort((a, b) => (place.get(key(a)) as number) - (place.get(key(b)) as number));
    for (const value of byPlace) {
      const colour = out.get(value) as string;
      if (used.has(colour)) out.set(value, palette.find((c) => !used.has(c)) as string);
      used.add(out.get(value) as string);
    }
  }
  return out;
}

// ── Direction ────────────────────────────────────────────────────────────

/** How an axis runs: in Hebrew a chart mirrors like the page - categories
 *  and time read from the right, the value axis stands at the right. The
 *  numbers themselves stay left-to-right (they are isolated where printed). */
export function axisDirection(isRtl: boolean): { reversed: boolean; valueAxisSide: 'left' | 'right' } {
  return { reversed: isRtl, valueAxisSide: isRtl ? 'right' : 'left' };
}
